import { TrendingUp, ArrowUpRight } from "lucide-react";

/**
 * Premium animated growth chart (pure SVG + CSS).
 * - Emerald→cyan gradient bars with staggered ease-out rise
 * - Gradient line with draw-on reveal using stroke-dashoffset
 * - Gradient area fill, glowing peak dot, subtle grid
 */
export default function GrowthChart() {
  const bars = [22, 30, 28, 40, 38, 52, 48, 64, 70, 82, 78, 94];

  const linePoints = [
    [0, 95], [30, 88], [60, 92], [90, 75], [120, 78],
    [150, 60], [180, 64], [210, 45], [240, 38], [270, 22], [300, 10],
  ];
  const linePath = linePoints
    .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
    .join(" ");
  const areaPath = `${linePath} L 300 120 L 0 120 Z`;

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.08),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(59,130,246,0.08),transparent_60%),linear-gradient(180deg,#050b1f_0%,#0a1429_100%)] p-4 sm:p-5">
      {/* subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(94,234,212,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(94,234,212,0.06) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
        aria-hidden
      />

      {/* header */}
      <div className="relative flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-emerald-300">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            Our growth
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-300">
          <ArrowUpRight className="h-3.5 w-3.5" />
          +248%
        </div>
      </div>

      {/* chart area */}
      <div className="relative mt-4 h-44 sm:h-52">
        <svg
          viewBox="0 0 300 140"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          aria-hidden
        >
          <defs>
            <linearGradient id="bar-grad-premium" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity="1" />
              <stop offset="55%" stopColor="#22d3ee" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.25" />
            </linearGradient>
            <linearGradient id="bar-grad-peak" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a7f3d0" stopOpacity="1" />
              <stop offset="50%" stopColor="#34d399" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.5" />
            </linearGradient>
            <linearGradient id="line-grad-premium" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="50%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#a5f3fc" />
            </linearGradient>
            <linearGradient id="area-grad-premium" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
            </linearGradient>
            <filter id="soft-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* baseline */}
          <line x1="0" y1="119.5" x2="300" y2="119.5" stroke="rgba(148,163,184,0.22)" strokeDasharray="2 3" />

          {/* bars */}
          <g>
            {bars.map((h, i) => {
              const barW = 16;
              const gap = 8;
              const totalW = bars.length * (barW + gap) - gap;
              const startX = (300 - totalW) / 2;
              const x = startX + i * (barW + gap);
              const height = (h / 100) * 110;
              const y = 120 - height;
              const isPeak = i === bars.length - 1;
              return (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width={barW}
                  height={height}
                  rx="3"
                  fill={isPeak ? "url(#bar-grad-peak)" : "url(#bar-grad-premium)"}
                  className="bar-rise"
                  style={{
                    transformOrigin: `${x + barW / 2}px 120px`,
                    animationDelay: `${i * 100}ms`,
                    filter: isPeak ? "drop-shadow(0 0 6px rgba(52,211,153,0.55))" : "none",
                  }}
                />
              );
            })}
          </g>

          {/* area */}
          <path d={areaPath} fill="url(#area-grad-premium)" className="area-fade" />

          {/* line (draw-on via stroke-dashoffset) */}
          <path
            d={linePath}
            fill="none"
            stroke="url(#line-grad-premium)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="line-draw"
            filter="url(#soft-glow)"
          />

          {/* point markers */}
          {linePoints.map(([x, y], i) => {
            const isLast = i === linePoints.length - 1;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={isLast ? 4.5 : 2}
                fill={isLast ? "#fff" : "#99f6e4"}
                stroke={isLast ? "#34d399" : "none"}
                strokeWidth="2"
                className="point-dot"
                style={{ animationDelay: `${1800 + i * 60}ms` }}
              />
            );
          })}

          {/* glowing peak pulse */}
          <circle cx="300" cy="10" r="5" fill="#34d399" className="pulse-dot" />
        </svg>

        {/* floating peak annotation */}
        <div className="absolute -right-1 top-0 flex items-center gap-1.5 rounded-lg border border-emerald-400/40 bg-slate-900/85 px-2 py-1 text-[11px] font-semibold text-emerald-300 backdrop-blur-sm opacity-0 animate-[fadeInUp_0.5s_ease-out_2.6s_forwards]">
          <TrendingUp className="h-3 w-3" />
          Peak growth
        </div>
      </div>

      {/* x-axis labels */}
      <div className="relative mt-2 flex justify-between px-4 text-[10px] font-medium uppercase tracking-widest text-slate-500">
        {["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"].map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>

      <style>{`
        .bar-rise {
          transform: scaleY(0);
          opacity: 0;
          animation: barRise 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes barRise {
          0% { transform: scaleY(0); opacity: 0; }
          60% { opacity: 1; }
          100% { transform: scaleY(1); opacity: 1; }
        }

        .line-draw {
          stroke-dasharray: 700;
          stroke-dashoffset: 700;
          animation: drawLine 2s cubic-bezier(0.22, 1, 0.36, 1) 1.2s forwards;
        }
        @keyframes drawLine {
          to { stroke-dashoffset: 0; }
        }

        .area-fade {
          opacity: 0;
          animation: areaFade 1.4s ease-out 2s forwards;
        }
        @keyframes areaFade {
          to { opacity: 1; }
        }

        .point-dot {
          opacity: 0;
          animation: dotIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @keyframes dotIn {
          0% { opacity: 0; transform: scale(0); transform-origin: center; }
          100% { opacity: 1; transform: scale(1); }
        }

        .pulse-dot {
          opacity: 0;
          animation: pulseIn 0.5s ease-out 3s forwards, pulseGlow 2.2s ease-in-out 3.6s infinite;
          transform-origin: 300px 10px;
        }
        @keyframes pulseIn { to { opacity: 1; } }
        @keyframes pulseGlow {
          0%, 100% { filter: drop-shadow(0 0 2px #34d399); }
          50% { filter: drop-shadow(0 0 12px #6ee7b7); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
